// Comment box with a small formatting toolbar and @mention suggestions.
// The body is plain text with light markdown; a chosen teammate is inserted as
// @[Name](user:id) so the server can notify them (CommentBody renders it).
import { useMemo, useRef, useState } from 'react'
import { AtSign, Bold, Italic, Link2, List } from 'lucide-react'
import { Button } from '../../components/ui/button'
import { useT } from '../../i18n'
import { staffTasksI18n } from '../../i18n/shared/staff-tasks'
import type { TaskPerson } from './api'
import { TEXTAREA_CLASS } from './TaskFormDialog'

const MENTION_QUERY_RE = /(^|\s)@([^\s@[\]]{0,30})$/u
const MAX_SUGGESTIONS = 6
const SEND_SHORTCUT =
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘+Enter' : 'Ctrl+Enter'

type Props = {
  value: string
  onChange: (value: string) => void
  onSubmit: () => void
  people: TaskPerson[]
  disabled?: boolean
  submitLabel: string
  placeholder: string
}

export function CommentComposer({
  value,
  onChange,
  onSubmit,
  people,
  disabled,
  submitLabel,
  placeholder,
}: Props) {
  const t = useT(staffTasksI18n)
  const ref = useRef<HTMLTextAreaElement>(null)
  const [caret, setCaret] = useState(0)
  const [active, setActive] = useState(0)
  const [dismissed, setDismissed] = useState<string | null>(null)

  const query = useMemo(() => {
    const match = MENTION_QUERY_RE.exec(value.slice(0, caret))
    return match ? match[2] : null
  }, [value, caret])
  const suggestions = useMemo(() => {
    if (query === null) return []
    const needle = query.toLocaleLowerCase()
    return people
      .filter((p) => p.display_name.toLocaleLowerCase().includes(needle))
      .slice(0, MAX_SUGGESTIONS)
  }, [people, query])
  const menuOpen = query !== null && dismissed !== `${caret}:${query}`

  const syncCaret = () => setCaret(ref.current?.selectionStart ?? value.length)

  const replaceRange = (start: number, end: number, insert: string, cursorOffset = insert.length) => {
    const next = value.slice(0, start) + insert + value.slice(end)
    onChange(next)
    const position = start + cursorOffset
    requestAnimationFrame(() => {
      ref.current?.focus()
      ref.current?.setSelectionRange(position, position)
      setCaret(position)
    })
  }

  const wrap = (before: string, after: string, fallback: string) => {
    const el = ref.current
    const start = el?.selectionStart ?? value.length
    const end = el?.selectionEnd ?? value.length
    const selected = value.slice(start, end) || fallback
    replaceRange(start, end, `${before}${selected}${after}`, before.length + selected.length + after.length)
  }

  const bulletize = () => {
    const el = ref.current
    const start = el?.selectionStart ?? value.length
    const end = el?.selectionEnd ?? value.length
    const lineStart = value.lastIndexOf('\n', start - 1) + 1
    const block = value.slice(lineStart, end)
    const bulleted = block
      .split('\n')
      .map((line) => (line.startsWith('- ') ? line : `- ${line}`))
      .join('\n')
    replaceRange(lineStart, end, bulleted)
  }

  const pick = (person: TaskPerson) => {
    const before = value.slice(0, caret)
    const atIndex = before.lastIndexOf('@')
    replaceRange(atIndex, caret, `@[${person.display_name}](user:${person.user_id}) `)
    setActive(0)
  }

  const onKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (menuOpen && suggestions.length > 0) {
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        setActive((i) => (i + 1) % suggestions.length)
        return
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault()
        setActive((i) => (i - 1 + suggestions.length) % suggestions.length)
        return
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault()
        pick(suggestions[Math.min(active, suggestions.length - 1)])
        return
      }
    }
    if (menuOpen && event.key === 'Escape') {
      event.preventDefault()
      setDismissed(`${caret}:${query}`)
      return
    }
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault()
      if (value.trim()) onSubmit()
    }
  }

  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={(e) => {
        e.preventDefault()
        if (value.trim()) onSubmit()
      }}
    >
      <div className="flex items-center gap-0.5" role="toolbar" aria-label={t.composer.hint}>
        <ToolButton label={t.composer.bold} onClick={() => wrap('**', '**', t.composer.bold)}>
          <Bold className="h-4 w-4" aria-hidden />
        </ToolButton>
        <ToolButton label={t.composer.italic} onClick={() => wrap('*', '*', t.composer.italic)}>
          <Italic className="h-4 w-4" aria-hidden />
        </ToolButton>
        <ToolButton label={t.composer.list} onClick={bulletize}>
          <List className="h-4 w-4" aria-hidden />
        </ToolButton>
        <ToolButton label={t.composer.link} onClick={() => wrap('[', '](https://)', t.composer.link)}>
          <Link2 className="h-4 w-4" aria-hidden />
        </ToolButton>
        <ToolButton
          label={t.composer.mention}
          onClick={() => {
            const at = caret > 0 && !/\s/.test(value[caret - 1] ?? ' ') ? ' @' : '@'
            replaceRange(caret, caret, at)
          }}
        >
          <AtSign className="h-4 w-4" aria-hidden />
        </ToolButton>
      </div>
      <div className="relative">
        <textarea
          ref={ref}
          rows={3}
          maxLength={5000}
          aria-label={placeholder}
          placeholder={placeholder}
          className={TEXTAREA_CLASS}
          value={value}
          onChange={(e) => {
            onChange(e.target.value)
            setCaret(e.target.selectionStart)
            setActive(0)
          }}
          onKeyDown={onKeyDown}
          onKeyUp={syncCaret}
          onClick={syncCaret}
          aria-autocomplete="list"
          aria-expanded={menuOpen}
          aria-controls="staff-task-mention-list"
        />
        {menuOpen && (
          <ul
            id="staff-task-mention-list"
            role="listbox"
            data-testid="mention-suggestions"
            className="absolute left-0 top-full z-[var(--z-dropdown)] mt-1 w-64 overflow-hidden rounded-[var(--radius-control)] border border-[var(--surface-lifted-border)] bg-[var(--surface-lifted-bg)] py-1 shadow-[var(--surface-lifted-shadow)]"
          >
            {suggestions.length === 0 ? (
              <li className="px-3 py-1.5 text-xs text-[var(--color-text-muted)]">{t.composer.noMatch}</li>
            ) : (
              suggestions.map((person, i) => (
                <li
                  key={person.user_id}
                  role="option"
                  aria-selected={i === active}
                  onMouseDown={(e) => {
                    e.preventDefault()
                    pick(person)
                  }}
                  className={`cursor-pointer px-3 py-1.5 text-sm ${i === active ? 'bg-[var(--color-surface-muted)]' : ''}`}
                >
                  {person.display_name}
                </li>
              ))
            )}
          </ul>
        )}
      </div>
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-[var(--color-text-muted)]">
          {t.composer.hint}
          <span className="max-sm:hidden"> · {t.composer.sendHint(SEND_SHORTCUT)}</span>
        </span>
        <Button type="submit" size="sm" disabled={!value.trim() || disabled}>
          {submitLabel}
        </Button>
      </div>
    </form>
  )
}

function ToolButton({
  label,
  onClick,
  children,
}: {
  label: string
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className="rounded p-1.5 text-[var(--color-text-muted)] hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-text)]"
    >
      {children}
    </button>
  )
}
